import styled from "@emotion/styled";
import { Typography as MuiTypography, TypographyProps } from "@mui/material";

import { theme } from "../mui-customs/Theme";

type variant =
  | "h1"
  | "h2"
  | "h3"
  | "body1"
  | "body2"
  | "body3"
  | "body4"
  | "body5"
  | "title1"
  | "title2"
  | "title3"
  | "title4"
  | "title5"
  | "title6"
  | "small1"
  | "small2"
  | "small3"
  | "small4"
  | "small5"
  | "verySmall1"
  | "verySmall2"
  | "verySmall3"
  | "verySmall4"
  | "verysmall5"
  | "btnDefault"
  | "btnMedium"
  | "inputFieldRegular";

type ExtendedTypographyProps =
  | Omit<TypographyProps, "variant"> & {
      variant:
        | {
            xs?: variant;
            sm?: variant;
            md?: variant;
            lg?: variant;
            xl?: variant;
          }
        | variant;
      dangerouslySetInnerHTML?: { __html: string };
    };

function addVariant(variant: variant) {
  let reUseStyle: string;
  switch (variant) {
    case "h1":
      reUseStyle = `
        font-weight: 500;
        font-size: 32px;
        line-height: 150%;
      `;
      break;
    case "h2":
      reUseStyle = `
        font-weight: 500;
        font-size: 24px;
        line-height: 150%;
      `;
      break;
    case "h3":
      reUseStyle = `
        font-weight: 500;
        font-size: 18px;
        line-height: 150%;      
        color: ${theme.black[800]};
      `;
      break;
    case "btnMedium":
      reUseStyle = `
        font-weight: 500;
        font-size: 16px;
        line-height: 24px;
        text-align: center;
        color: ${theme.whiteBg.WBG1};
      `;
    case "btnDefault":
      reUseStyle = `
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
      `;
      break;
    case "title5":
      reUseStyle = `
        font-weight: 500;
        font-size: 18px;
        line-height: 24px;
        color: ${theme.black[800]};
      `;
      break;
    case "verySmall1":
      reUseStyle = `
        font-weight: 500;
        font-size: 12px;
        line-height: 24px;    
        color: ${theme.grey2[400]};
      `;
    case "verySmall2":
      reUseStyle = `
        font-weight: 400;
        font-size: 12px;
        line-height: 20px;    
      `;
      break;
    case "verySmall4":
      reUseStyle = `
      font-weight: 400;
      font-size: 12px;
      line-height: 20px; 
      color: ${theme.grey2[500]};

    `;
      break;
    case "verysmall5":
      reUseStyle = `
        font-weight: 500;
        font-size: 8px;
        line-height: 12px;
        `;
      break;
    case "body2":
      reUseStyle = `
        font-weight: 400;
        font-size: 14px;
        line-height: 24px;   
        color: ${theme.grey2[400]};
      `;
      break;
    case "body3":
      reUseStyle = `
        font-weight: 400;
        font-size: 14px;
        line-height: 21px;   
      `;
      break;
    case "title1":
      reUseStyle = `
        font-weight: 500;
        font-size: 14px;
        line-height: 24px;
        color: ${theme.black[400]};
      `;
      break;
    case "body5":
      reUseStyle = `
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
      `;
      break;
    case "title2":
      reUseStyle = `
        font-weight: 500;
        font-size: 14px;
        line-height: 24px;
      `;
      break;
    case "title3":
      reUseStyle = `
        font-weight: 600;
        font-size: 18px;
        line-height: 22px;
        `;
      break;
    case "title4":
      reUseStyle = `
          font-weight: 600;
          font-size: 18px;
          line-height: 24px;
          `;
      break;
    case "title6":
      reUseStyle = `
        font-weight: 500;
        font-size: 20px;
        line-height: 24px;
      `;
      break;
    case "small2":
      reUseStyle = `
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        color: ${theme.grey2[400]};
      `;
      break;
    case "small4":
      reUseStyle = `
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
      `;
      break;
    case "small5":
      reUseStyle = `
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      `;
      break;

    default:
      reUseStyle = `
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
      `;
      break;
  }
  return reUseStyle;
}

const Typography = ({
  children,
  variant,
  sx,
  dangerouslySetInnerHTML,
  ...props
}: ExtendedTypographyProps) => {
  const StyledComponent = styled(MuiTypography)`
    font-style: normal;
    font-family: "Poppins", sans-serif;
    @media only screen and (min-width: 0px) {
      ${typeof variant === "object" && variant.xs
        ? addVariant(variant.xs)
        : typeof variant === "string"
        ? addVariant(variant)
        : ""}
    }
    @media only screen and (min-width: 600px) {
      ${typeof variant === "object" && variant.sm ? addVariant(variant.sm) : ""}
    }
    @media only screen and (min-width: 900px) {
      ${typeof variant === "object" && variant.md ? addVariant(variant.md) : ""}
    }
    @media only screen and (min-width: 1200px) {
      ${typeof variant === "object" && variant.lg ? addVariant(variant.lg) : ""}
    }
    @media only screen and (min-width: 1200px) {
      ${typeof variant === "object" && variant.xl ? addVariant(variant.xl) : ""}
    }
  `;

  if (dangerouslySetInnerHTML) {
    return (
      <StyledComponent
        {...props}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      />
    );
  }

  return (
    <StyledComponent sx={sx} {...props}>
      {children}
    </StyledComponent>
  );
};

export default Typography;
